import React, { useState, useEffect } from "react";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";

const EditVaccinationAndMortality = ({ 
  vaccinationData = [], 
  householdListings = [],
  DqaApproved = false,
  DqaRejected = false
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [vaccinationRecords, setVaccinationRecords] = useState([]);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);

  // Initialize with prop data
  useEffect(() => {
    
    setIsDqaApproved(DqaApproved);
    setIsDqaRejected(DqaRejected)
    
    // First, get all children under 5 from household listings
    const childrenUnder5 = householdListings.filter(member => {
      const age = parseInt(member.age);
      return !isNaN(age) && age >= 0 && age < 5;
    });

    if (childrenUnder5.length === 0) {
      setChildren([]);
      setLoading(false);
      return;
    }

    const mappedChildren = childrenUnder5.map((child) => {
      const vaccinationRecord = child.Vaccination;

      return {
        household_child_vaccination_id: vaccinationRecord?.household_child_vaccination_id || null,
        household_listing_id: child.household_listing_id,
        name: child.household_name || `Child`,
        age: child.age,
        gender: child.gender || "",
        vaccinated: vaccinationRecord ? 
          (vaccinationRecord.received_all_vaccinations ? "yes" : "no") : 
          ""
      };
    });
    
    setChildren(mappedChildren);
    
    // Set vaccination records for potential updates
    const existingVaccinations = childrenUnder5
      .map(child => child.Vaccination)
      .filter(vaccination => vaccination !== null);
    
    setVaccinationRecords(existingVaccinations);
    setLoading(false);
  }, [vaccinationData, householdListings, DqaApproved]);

  const handleChildChange = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;
    setChildren(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setUpdating(true);
      
      // Filter children with vaccination data
      const childrenWithVaccination = children.filter(child => child.vaccinated !== "");
      
      if (childrenWithVaccination.length === 0) {
        toast.warning("Please select vaccination status for at least one child");
        return;
      }

      const vaccinationPayload = {
        householdChildVaccination: childrenWithVaccination.map(child => ({
          household_listing_id: child.household_listing_id,
          received_all_vaccinations: child.vaccinated === "yes" ? 1 : 0,
          status: 1
        }))
      };
      
      const hasExistingRecords = vaccinationRecords.length > 0;
      
      let response;
      if (hasExistingRecords) {
        const firstVaccinationId = vaccinationRecords[0]?.household_child_vaccination_id;
        
        response = await fetchData(
          `household-childvaccination-update/${firstVaccinationId}`,
          "PUT",
          vaccinationPayload,
          {},
          {},
          navigate,
          "/household",
          dispatch
        );
      } else {
        // Create new records
        response = await fetchData(
          `household-childvaccination-store`,
          "POST",
          vaccinationPayload,
          {},
          {},
          navigate,
          "/household",
          dispatch
        );
      }

      if (!response?.success) {
        throw new Error(response?.message || "Failed to save vaccination data");
      }

      toast.success("Vaccination data saved successfully");
      
    } catch (error) {
      console.error("Error saving vaccination data:", error);
      toast.error(error.message || "Error saving vaccination data");
    } finally {
      setUpdating(false);
    }
  };

  const isDataValid = children.length > 0 && children.some(child => 
    child.vaccinated !== ""
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading vaccination information...</div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}
      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Vaccination Status
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Vaccination Status
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      {children.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No children under 5 found in this household
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="border p-4 rounded-xl shadow-sm">
            <div className="space-y-6">
              {children.map((child, index) => (
                <div
                  key={`child-${child.household_listing_id}`}
                  className="border-b border-gray-200 pb-4 last:border-b-0"
                >
                  <h4 className="text-md font-medium text-gray-800 mb-4">
                    Child {index + 1}: {child.name}
                  </h4>

                  <form className="w-full">
                    <div className="grid grid-cols-1 gap-4">
                      {/* First row - Basic info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <ThinTextField
                            label="Child Name"
                            isRequired={false}
                            value={child.name}
                            onChange={() => {}}
                            disabled={true}
                          />
                        </div>

                        <div>
                          <ThinTextField
                            label="Age"
                            isRequired={false}
                            value={child.age?.toString() || ""}
                            onChange={() => {}}
                            disabled={true}
                          />
                        </div>

                        <div>
                          <ThinTextField
                            label="Gender"
                            isRequired={false}
                            value={child.gender}
                            onChange={() => {}}
                            disabled={true}
                          />
                        </div>
                      </div>

                      {/* Second row - Vaccination question */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Has this child received all mandatory vaccinations?
                        </label>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`vaccination_${child.household_listing_id}`}
                              value="yes"
                              checked={child.vaccinated === "yes"}
                              onChange={(e) =>
                                handleChildChange(
                                  index,
                                  "vaccinated",
                                  e.target.value,
                                )
                              }
                              disabled={isDqaApproved || isDqaRejected}
                              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 disabled:opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Yes
                            </span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`vaccination_${child.household_listing_id}`}
                              value="no"
                              checked={child.vaccinated === "no"}
                              onChange={(e) =>
                                handleChildChange(
                                  index,
                                  "vaccinated",
                                  e.target.value,
                                )
                              }
                              disabled={isDqaApproved || isDqaRejected}
                              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 disabled:opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isDataValid && !isDqaApproved && !isDqaRejected && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={updating}
            className={`${
              updating ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
            } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {updating ? "Updating..." : "Update"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EditVaccinationAndMortality;